import type { SystemInstance } from '@/api/hooks/useTenantConfig'
import type { components } from '@/types/api/v1'
import { Form, Input, Modal, Select, Switch } from 'antd'
import { useEffect } from 'react'

type ListenMode = components['schemas']['ListenMode']

export interface CrossroadsIntegrationValues {
  redpanda_topic: string | null
  listen_mode: ListenMode
  ingest_tank_readings: boolean
  prod_instance: boolean
  block_backwards_status_updates: boolean
}

interface CrossroadsIntegrationModalProps {
  open: boolean
  onClose: () => void
  instance: SystemInstance | null
  /** Whether the instance's system is Gravitate S&D. Resolved by the grid from
   * the systems list rather than from the instance's own `system_name`, which
   * is free text and is only ever set when the instance is first created. */
  isSupplyAndDispatch: boolean
  onSave: (values: CrossroadsIntegrationValues) => void
  loading?: boolean
}

/** The stored values stay prod/test/never -- only the wording is about which
 * Crossroads environment is on the other end. */
const SEND_REQUESTS_TO_OPTIONS = [
  { label: 'Crossroads Prod', value: 'prod' },
  { label: 'Crossroads Test', value: 'test' },
  { label: 'Neither', value: 'never' },
]

export function CrossroadsIntegrationModal({
  open,
  onClose,
  instance,
  isSupplyAndDispatch,
  onSave,
  loading,
}: CrossroadsIntegrationModalProps) {
  const [form] = Form.useForm()

  const named = instance?.name || 'this instance'

  const listenMode =
    (Form.useWatch('listen_mode', form) as ListenMode | undefined) ??
    instance?.listen_mode ??
    'never'
  const selectedTarget = SEND_REQUESTS_TO_OPTIONS.find(
    (option) => option.value === listenMode && option.value !== 'never',
  )
  const blockedTargets = SEND_REQUESTS_TO_OPTIONS.filter(
    (option) => option.value !== 'never' && option.value !== listenMode,
  )
    .map((option) => option.label)
    .join(' or ')
  const sendRequestsToExtra = selectedTarget
    ? `${named} will send requests to ${selectedTarget.label}. Requests sent by ${named} to ${blockedTargets} will be blocked.`
    : `${named} will not send requests to Crossroads. Requests sent by ${named} to ${blockedTargets} will be blocked.`

  useEffect(() => {
    if (open && instance) {
      form.setFieldsValue({
        redpanda_topic: instance.redpanda_topic || '',
        listen_mode: instance.listen_mode || 'never',
        ingest_tank_readings: instance.ingest_tank_readings ?? false,
        prod_instance: instance.prod_instance ?? false,
        block_backwards_status_updates:
          instance.block_backwards_status_updates ?? false,
      })
    }
  }, [open, instance, form])

  const handleSave = () => {
    form.validateFields().then((values) => {
      onSave({
        // The S&D-only fields are not rendered for other systems, so they come
        // back undefined -- fall back to what is stored rather than to a
        // default, which would silently clear them.
        redpanda_topic: isSupplyAndDispatch
          ? values.redpanda_topic || null
          : (instance?.redpanda_topic ?? null),
        listen_mode: values.listen_mode,
        ingest_tank_readings: isSupplyAndDispatch
          ? (values.ingest_tank_readings ?? false)
          : (instance?.ingest_tank_readings ?? false),
        prod_instance: values.prod_instance ?? false,
        block_backwards_status_updates:
          values.block_backwards_status_updates ?? false,
      })
    })
  }

  return (
    <Modal
      title={`Crossroads Integration - ${instance?.name || ''}`}
      open={open}
      onOk={handleSave}
      onCancel={onClose}
      okText='Save'
      confirmLoading={loading}
      width={680}
      styles={{ header: { textAlign: 'center', paddingBottom: 16 } }}
      destroyOnClose
    >
      <Form
        form={form}
        layout='horizontal'
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        labelAlign='left'
        labelWrap
        colon={false}
      >
        <Form.Item
          name='listen_mode'
          label='Send Requests To'
          extra={sendRequestsToExtra}
        >
          <Select options={SEND_REQUESTS_TO_OPTIONS} />
        </Form.Item>
        <Form.Item
          name='prod_instance'
          label='Live in Production'
          valuePropName='checked'
          extra={`When enabled, ${named} will appear as a Prod instance when filtering orders, logs, or dashboard metrics.`}
        >
          <Switch />
        </Form.Item>
        <Form.Item
          name='block_backwards_status_updates'
          label='Prevent Backwards Status Updates'
          valuePropName='checked'
          extra={`When enabled, status updates sent to ${named} will be blocked if they would move the order status backwards.`}
        >
          <Switch />
        </Form.Item>
        {isSupplyAndDispatch && (
          <Form.Item
            name='ingest_tank_readings'
            label='Send Tank Readings'
            extra={`When enabled, S&D will send tank readings to Crossroads from ${named}.`}
            valuePropName='checked'
          >
            <Switch />
          </Form.Item>
        )}
        {isSupplyAndDispatch && (
          <Form.Item
            name='redpanda_topic'
            label='Send Orders From'
            extra={`Enter the name of the order messages queue for ${named}. Crossroads receives orders by listening to this queue.`}
          >
            <Input placeholder='Enter topic name' />
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}
